/* ============================================================
 * DASHBOARD DATE CONTROL — ONE CALENDAR CHIP (2026-10-14)
 * ------------------------------------------------------------
 * Visual rework of the shipped day / from -> to control (the two raw
 * dd/mm/yyyy inputs read as a separate widget). ONE chip-styled button that
 * sits with the other period chips and opens a small month grid. TWO clicks:
 *   - first click  -> the START day; the popover STAYS OPEN, that day
 *     highlighted, waiting for the end day. Nothing is applied yet;
 *   - second click on the SAME day  -> that single day, popover closes;
 *   - second click on a LATER day   -> the from -> to range, popover closes;
 *   - second click on an EARLIER day -> not a range: it replaces the start (the
 *     old one is discarded) and the popover keeps waiting. Dates are never swapped;
 *   - Clear -> back to Today. Clicking outside / Escape closes WITHOUT applying
 *     a half-selection — the pending first click is discarded.
 * It owns no period maths: it hands (from, to) — "yyyy-mm-dd" strings, to ""
 * for one day — to the dashboard, which writes "D<day>" / "R<from>_<to>" into
 * its one filter string exactly as before; dashPeriodOf validates and resolves
 * it. A START may not be after today or before `min` (the data floor); a range
 * END may lie in the future, as This Week's does.
 * Grid weeks run Saturday -> Friday, ARO's work week.
 * Mobile (<768px): the popover is a bottom sheet with a backdrop.
 * ============================================================ */
import React, { useEffect, useRef, useState } from "react";

var MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
var WEEKDAYS = ["Sat","Sun","Mon","Tue","Wed","Thu","Fri"];
var p2 = function (n) { return (n < 10 ? "0" : "") + n; };
var ymdOf = function (y, m, d) { return y + "-" + p2(m) + "-" + p2(d); };
var partsOf = function (s) { var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || ""); return m ? { y: +m[1], m: +m[2], d: +m[3] } : null; };
var mon3 = function (p) { return MONTHS[p.m - 1].slice(0, 3); };

// "7 Oct" (a day in the current year) / "7 Oct 2025" / "9–11 Oct 2026" /
// "28 Sep – 3 Oct 2026" / "28 Dec 2025 – 3 Jan 2026".
export function dashRangeChipLabel(from, to, todayYmd) {
  var a = partsOf(from), b = partsOf(to), t = partsOf(todayYmd);
  if (!a) return "Date";
  if (!b || from === to) return a.d + " " + mon3(a) + (t && a.y === t.y ? "" : " " + a.y);
  if (a.y === b.y && a.m === b.m) return a.d + "–" + b.d + " " + mon3(b) + " " + b.y;
  if (a.y === b.y) return a.d + " " + mon3(a) + " – " + b.d + " " + mon3(b) + " " + b.y;
  return a.d + " " + mon3(a) + " " + a.y + " – " + b.d + " " + mon3(b) + " " + b.y;
}

// What a click on `day` does, given the pending start (or null). Pure, so the
// verification harness can run it. A range only runs FORWARD, and dates are
// never swapped:
//   no pending          -> the day becomes the pending start, nothing applied
//   same day            -> that single day, applied
//   a day AFTER start   -> the range start -> day, applied
//   a day BEFORE start  -> NOT a range: it REPLACES the start (the old one is
//                          discarded) and the popover keeps waiting
export function dashRangeClick(pending, day) {
  if (!pending) return { pending: day, pick: null };
  if (day === pending) return { pending: null, pick: { from: day, to: "" } };
  if (day < pending) return { pending: day, pick: null };
  return { pending: null, pick: { from: pending, to: day } };
}

export default function DashRangePicker(props) {
  var v = props.value || { from: "", to: "" };
  var today = props.todayYmd, min = props.min || "";
  var active = !!props.active;
  // autoOpen (owner 2026-10-31, the Leads / Daily Request phone bar): the chip
  // mounts because the user just chose "📅 Date" in the period dropdown, so the
  // calendar opens at once instead of asking for a second tap.
  var [open, setOpen] = useState(!!props.autoOpen);
  // The first click of a two-click pick. Held here, applied to nothing, and
  // dropped whenever the popover closes without a second click.
  var [pending, setPending] = useState(null);
  var wrapRef = useRef(null);
  var close = function () { setPending(null); setOpen(false); };
  // Closed WITHOUT a pick (outside tap, Escape, the backdrop, the chip again):
  // onDismiss lets a host undo a period it selected only to show this calendar.
  var dismiss = function () { close(); if (props.onDismiss) props.onDismiss(); };
  var seed = partsOf(v.from) || partsOf(today) || { y: 2026, m: 1, d: 1 };
  var [view, setView] = useState({ y: seed.y, m: seed.m });

  useEffect(function () {
    if (!open) return;
    var onDown = function (e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) dismiss(); };
    var onKey = function (e) { if (e.key === "Escape") dismiss(); };
    // One tick late: an autoOpen chip mounts INSIDE the mousedown that chose
    // "📅 Date" in the phone dropdown (SearchableSelect commits on mousedown), and a
    // listener added now would receive that same event as an outside click and
    // close the calendar before it was ever seen.
    var t = setTimeout(function () {
      document.addEventListener("mousedown", onDown);
      document.addEventListener("keydown", onKey);
    }, 0);
    return function () { clearTimeout(t); document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  var toggle = function () {
    if (open) { dismiss(); return; }
    var s = partsOf(v.from) || partsOf(today); if (s) setView({ y: s.y, m: s.m });
    setPending(null);
    setOpen(true);
  };
  var pick = function (day) {
    var r = dashRangeClick(pending, day);
    if (!r.pick) { setPending(r.pending); return; }
    props.onPick(r.pick.from, r.pick.to);
    close();
  };
  var shift = function (delta) {
    var n = view.m - 1 + delta;
    setView({ y: view.y + Math.floor(n / 12), m: ((n % 12) + 12) % 12 + 1 });
  };

  // Month grid, Saturday-first.
  var first = new Date(Date.UTC(view.y, view.m - 1, 1));
  var lead = (first.getUTCDay() + 1) % 7;
  var dim = new Date(Date.UTC(view.y, view.m, 0)).getUTCDate();
  var cells = [];
  for (var i = 0; i < lead; i++) cells.push(null);
  for (var d = 1; d <= dim; d++) cells.push(ymdOf(view.y, view.m, d));
  // While a first click is pending, ONLY that day is highlighted; otherwise the
  // applied selection is.
  var lo = pending || v.from, hi = pending || v.to || v.from;
  if (lo && hi && lo > hi) { var tmp = lo; lo = hi; hi = tmp; }
  // A day is clickable unless it is before the floor, or it would START a
  // selection after today (once a start is pending, a future day is a range END).
  var clickable = function (day) {
    if (min && day < min) return false;
    if (day > today && !pending) return false;
    return true;
  };

  var chipPad = props.isMobile ? "8px 12px" : "6px 12px";
  var label = active ? dashRangeChipLabel(v.from, v.to, today) : "Date";
  var panel = (
    <div role="dialog" aria-label="Pick a day or a range" style={props.isMobile ? {
      position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 1001, background: "#fff",
      borderRadius: "16px 16px 0 0", padding: "14px 16px 20px", boxShadow: "0 -8px 30px rgba(0,0,0,0.18)"
    } : {
      position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 99, background: "#fff", width: 264,
      border: "1px solid #E2E8F0", borderRadius: 10, padding: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.08)"
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <button type="button" aria-label="Previous month" onClick={function () { shift(-1); }} style={navBtn}>{"‹"}</button>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>{MONTHS[view.m - 1] + " " + view.y}</div>
        <button type="button" aria-label="Next month" onClick={function () { shift(1); }} style={navBtn}>{"›"}</button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, textAlign: "center" }}>
        {WEEKDAYS.map(function (w) { return <div key={w} style={{ fontSize: 10, fontWeight: 700, color: "#94A3B8", padding: "2px 0" }}>{w}</div>; })}
        {cells.map(function (day, idx) {
          if (!day) return <div key={"b" + idx} />;
          var ok = clickable(day);
          var isEnd = day === lo || day === hi;
          var inside = lo && hi && day > lo && day < hi;
          return <button key={day} type="button" disabled={!ok} data-day={day} onClick={function () { pick(day); }}
            style={{
              height: props.isMobile ? 38 : 30, fontSize: 12, fontFamily: "inherit", borderRadius: 7, cursor: ok ? "pointer" : "default",
              border: day === today ? "1px solid #93C5FD" : "1px solid transparent",
              background: isEnd ? "#3B82F6" : inside ? "#DBEAFE" : "#fff",
              color: isEnd ? "#fff" : ok ? "#334155" : "#CBD5E1", fontWeight: isEnd ? 700 : 500
            }}>{+day.slice(8)}</button>;
        })}
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8, gap: 8 }}>
        <div style={{ fontSize: 10, color: "#94A3B8", lineHeight: 1.3 }}>
          {pending ? "Pick the end day \u2014 same day = single day" : "Pick the start day"}
        </div>
        <button type="button" onClick={function () { props.onPick("", ""); close(); }}
          style={{ fontSize: 12, fontWeight: 600, color: "#1D4ED8", background: "none", border: "none", cursor: "pointer", padding: "4px 2px", flexShrink: 0 }}>Clear</button>
      </div>
    </div>
  );
  return (
    <div ref={wrapRef} style={props.segment ? { position: "relative", display: "flex", width: "100%", minWidth: 0 } : { position: "relative", flex: (props.isMobile && !props.iconOnly) ? "1 1 auto" : "0 0 auto", flexShrink: 0 }}>
      {/* iconOnly (owner 2026-10-29, the Deals phone period row): a 40px square
          with just the 📅 — a quarter chip's height on the phone (the phone
          stylesheet's 40px tap target), titled "Date"; the picker and the state
          behind it are untouched.
          triggerStyle (owner 2026-10-31): the text chip wears the row it sits
          in — the Leads / Daily Request bar's smaller pill chips. Text chip only.
          segment (owner 2026-10-30): the same icon-only trigger drawn as the
          last cell of the phone period row's SegmentedControl — it fills its
          cell, has no border of its own, and turns white (the selected-segment
          look) while a range is active. Same toggle, same popover, same onPick. */}
      <button type="button" onClick={toggle} aria-haspopup="dialog" aria-expanded={open} title={props.iconOnly ? (active ? label : "Date") : undefined} data-date-chip={props.iconOnly ? "icon" : "text"} data-date-segment={props.segment ? (active ? "1" : "0") : undefined}
        style={Object.assign(props.segment ? {
          fontSize: 14, padding: "5px 2px", width: "100%", minWidth: 0, minHeight: 34, maxHeight: "none", borderRadius: 9, cursor: "pointer", lineHeight: 1.2,
          fontFamily: "inherit", whiteSpace: "nowrap", textAlign: "center", border: "none", boxSizing: "border-box",
          background: active ? "#fff" : "transparent", color: active ? "#1D4ED8" : "#64748B",
          boxShadow: active ? "0 1px 3px rgba(15,23,42,0.16), 0 0 0 0.5px rgba(15,23,42,0.04)" : "none", fontWeight: active ? 700 : 500
        } : props.iconOnly ? {
          fontSize: 12, padding: "5px 0", width: 40, minWidth: 40, minHeight: 40, height: 40, borderRadius: 8, cursor: "pointer", lineHeight: "normal",
          fontFamily: "inherit", whiteSpace: "nowrap", textAlign: "center", flexShrink: 0,
          border: active ? "1px solid #3B82F6" : "1px solid #E2E8F0",
          background: active ? "#EFF6FF" : "#fff", color: active ? "#1D4ED8" : "#64748B",
          fontWeight: active ? 600 : 500
        } : {
          fontSize: 12, padding: chipPad, minHeight: props.isMobile ? 36 : undefined, borderRadius: 8, cursor: "pointer",
          width: props.isMobile ? "100%" : "auto", fontFamily: "inherit", whiteSpace: "nowrap",
          border: active ? "1px solid #3B82F6" : "1px solid #E2E8F0",
          background: active ? "#EFF6FF" : "#fff", color: active ? "#1D4ED8" : "#64748B",
          fontWeight: active ? 600 : 500
        }, (!props.segment && !props.iconOnly && props.triggerStyle) || {})}>{props.iconOnly ? "📅" : "📅 " + label}</button>
      {open && props.isMobile && <div onClick={dismiss} style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(15,23,42,0.35)" }} />}
      {open && panel}
    </div>
  );
}

var navBtn = { width: 30, height: 30, fontSize: 18, lineHeight: 1, border: "1px solid #E2E8F0", background: "#fff", color: "#475569", borderRadius: 8, cursor: "pointer", fontFamily: "inherit" };
